<template>
  <div v-if="!!phaseData">
    <template v-if="!isLoaded">
      <v-sheet v-if="error" class="d-flex flex-column align-center justify-center row-gap-2" height="50vh">
        <template v-if="error">
          <h3>Um erro ocorreu ao tentar carregar os dados da tela!</h3>
          <v-btn class="flex-grow-0" large color="primary" @click="loadData"
            >tentar novamente <v-icon class="ml-2" large>mdi-refresh</v-icon></v-btn
          >
        </template>
        <template v-else>
          <h3>Carregando dados...</h3>
          <v-progress-circular indeterminate />
        </template>
      </v-sheet>
    </template>
    <template v-else>
      <v-switch
        v-model="emptyPrint"
        :value="defaultSwitchState"
        label="Imprimir modelo vazio"
        hint="Você pode editar esse modelo no navegador."
        persistent-hint
        class="mb-4"
      />
      <v-form v-model="valid" v-if="!emptyPrint">
        <field-label
          label="Medicamentos solicitados"
          titleColorClass="grey--text text--darken-2"
          class="mb-4"
          v-if="phaseData.medicines"
        >
          <v-card v-for="medicine in lmeMedicines" :key="medicine.key" class="px-4 gap-2 pt-4">
            <v-card-title>
              <span class="mt-4 grey--text text--darken-4 text-h6">{{ medicine.name }}</span>
            </v-card-title>
            <v-card-text>
              <field-label label="Informe a quantidade para cada mês">
                <v-row dense>
                  <v-col v-for="n in 6" :key="n">
                    <field-label :label="n + ' mês'">
                      <text-field
                        dense
                        type="number"
                        v-model="phaseData.medicines[medicine.name]['month' + n]"
                        v-if="phaseData.medicines[medicine.name]"
                      />
                    </field-label>
                  </v-col>
                </v-row>
              </field-label>
            </v-card-text>
          </v-card>
          <field-label label="Medicamentos anteriores" titleColorClass="grey--text text--darken-2" class="mt-4">
            <template #label-extra>
              <v-btn @click="resetMedicineText" x-small color="primary" :disabled="isFinishedAttendance"
                >Gerar novamente</v-btn
              >
            </template>
            <text-area
              :readonly="disabled"
              v-model="phaseData.previousMedicineText"
              :max-length="500"
              :rows="2"
              class="mt-2"
            />
          </field-label>
        </field-label>
        <v-row dense>
          <v-col>
            <field-label label="Local" titleColorClass="grey--text text--darken-2" required>
              <select-field
                :items="attendance.physicianCnes"
                v-model="phaseData.local"
                :itemText="(i) => `${i.code} - ${i.name}`"
                itemValue="id"
                dense
                :rules="[$validation.required()]"
                :disabled="isFinishedAttendance" /></field-label
          ></v-col>
        </v-row>
        <v-row dense>
          <v-col
            ><field-label label="Nome da mãe do paciente" titleColorClass="grey--text text--darken-2" required>
              <text-field
                v-model="phaseData.patientMotherName"
                dense
                :rules="[$validation.required()]"
                :disabled="isFinishedAttendance"
                readonly
              /> </field-label
          ></v-col>
          <v-col
            ><field-label label="Cor de pele do paciente" titleColorClass="grey--text text--darken-2" required>
              <select-field
                :items="skinColors"
                v-model="phaseData.patientSkinColor"
                itemText="text"
                itemValue="value"
                dense
                :rules="[$validation.required()]"
                :disabled="isFinishedAttendance"
              /> </field-label
          ></v-col>
        </v-row>
        <v-row dense>
          <v-col>
            <field-label label="CID" titleColorClass="grey--text text--darken-2" required>
              <select-field
                :items="lmeCids"
                v-model="phaseData.cid"
                :itemText="(i) => `${i.code} - ${i.name}`"
                itemValue="id"
                dense
                :rules="[$validation.required()]"
                return-object
                @change="resetAnamnesis"
                :disabled="isFinishedAttendance" /></field-label
          ></v-col>
        </v-row>
        <v-row dense>
          <v-col
            ><field-label label="Atestado de incapacidade?" titleColorClass="grey--text text--darken-2">
              <v-switch
                v-model="phaseData.incapacity"
                @change="resetResponsible"
                dense
                :disabled="isFinishedAttendance"
              /> </field-label
          ></v-col>

          <v-col v-if="phaseData.incapacity"
            ><field-label label="Responsável" titleColorClass="grey--text text--darken-2" required>
              <text-field
                v-model="phaseData.responsibleName"
                dense
                :rules="[$validation.required()]"
                :disabled="isFinishedAttendance"
              /> </field-label
          ></v-col>
        </v-row>
        <v-row dense>
          <v-col
            ><field-label label="Imprimir data atual" titleColorClass="grey--text text--darken-2">
              <v-switch v-model="phaseData.printCurrentDate" dense /> </field-label
          ></v-col>
        </v-row>
        <v-row dense>
          <v-col
            ><field-label label="Anti-TNF" titleColorClass="grey--text text--darken-2">
              <radio-options
                :options="antiTnfOptions"
                row
                v-model="phaseData.antitnf"
                @change="resetAnamnesis"
                :disabled="isFinishedAttendance" /></field-label
          ></v-col>
        </v-row>
        <v-row dense>
          <v-col
            ><field-label label="Anamnesis" titleColorClass="grey--text text--darken-2" required>
              <template #label-extra>
                <v-btn @click="resetAnamnesis" x-small color="primary" :disabled="isFinishedAttendance"
                  >Gerar novamente</v-btn
                >
              </template>
              <text-area
                class="mt-2"
                v-model="phaseData.anamnesis"
                :rules="[$validation.required()]"
                :disabled="isFinishedAttendance" /></field-label
          ></v-col>
        </v-row>
        <v-row dense class="d-flex">
          <v-btn color="primary" text v-if="!!lmeStateDocsUrl" :href="lmeStateDocsUrl"
            >Baixar documentos do estado de {{ attendance.patientStateShortName }}</v-btn
          >
        </v-row>
      </v-form>
      <download-frame
        :url="emptyPrint ? printEmptyLmeUrl : printLmeUrl"
        :remoteUrl="!emptyPrint"
        :disabled="!valid && !emptyPrint"
        class="ml-auto"
        :onBeforeDownload="() => saveLme(emptyPrint)"
        alwaysForceDownload
      >
        <template #default="{ loading }">
          <v-btn color="primary" :disabled="!valid && !emptyPrint" :loading="loading">Imprimir</v-btn>
        </template>
      </download-frame>
    </template>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import FieldLabel from '@/components/FieldLabel.vue'

import SelectField from '@/components/SelectField.vue'
import TextField from '@/components/TextField.vue'

import axialAsasMixin from '@/components/AttendanceForm/espondiloartrites/mixins/espondiloartritesAxialAsasMixin'
import perifericAsasMixin from '@/components/AttendanceForm/espondiloartrites/mixins/espondiloartritesPerifericAsasMixin'
import { LME_STATE_DOCS } from '@/components/AttendanceForm/general/constants/lme'
import psoriasisSummaryMixin from '@/components/AttendanceForm/psoriasis/mixins/psoriasisSummaryMixin'
import DownloadFrame from '@/components/DownloadFrame.vue'
import RadioOptions from '@/components/RadioOptions.vue'
import TextArea from '@/components/TextArea.vue'
import { STATUS_FINISHED } from '@/constants/attendance'
import { DISEASES } from '@/constants/disease'
import { PUBLIC_STORAGE_URL } from '@/constants/global'
import { SKIN_COLOR } from '@/constants/person'

const {
  mapGetters: attendanceGettersMap,
  mapActions: attendanceActionsMap,
  mapState: attendanceStateMap,
} = createNamespacedHelpers('attendance')

export default {
  mixins: [axialAsasMixin, perifericAsasMixin, psoriasisSummaryMixin],

  extends: BasePhase,
  components: {
    FieldLabel,
    SelectField,
    TextField,
    RadioOptions,
    TextArea,
    DownloadFrame,
  },
  computed: {
    ...attendanceGettersMap(['getPhaseDataByKey', 'patientData']),
    ...attendanceStateMap(['isServerSyncedData', 'isLocalSyncedData', 'isSavingData', 'attendance']),

    lmeStateDocsUrl() {
      return LME_STATE_DOCS[this.attendance.patientStateShortName]
    },
    selectedTreatments() {
      if (!this.getPhaseDataByKey('medicamentos') || !this.getPhaseDataByKey('medicamentos').selectedTreatments) {
        return []
      }
      return [...this.getPhaseDataByKey('medicamentos').selectedTreatments]
    },
    isFinishedAttendance() {
      return this.attendance?.status === STATUS_FINISHED.value
    },
    lmeMedicines() {
      return this.selectedTreatments
        .filter((t) => t.selectedDosage.lme?.showLmeInAttendance)
        .map((t) => {
          let quantities = []

          for (let i = 1; i < 7; i++) {
            quantities.push(t.selectedDosage.lme[t.selectedDosage.key + 'Month' + i])
          }
          return { name: t.medicine.name, quantities: quantities, lmeId: t.selectedDosage.lme.id }
        })
    },
    lmeCids() {
      let cids = []
      this.selectedTreatments
        .filter((t) => t.selectedDosage.lme?.showLmeInAttendance)
        .forEach((t) => {
          t.selectedDosage.lme.cids?.forEach((c) => {
            if (!cids.find((cid) => cid.id === c.id)) {
              cids.push(c)
            }
          })
        })
      return cids
    },
    skinColors() {
      return [...SKIN_COLOR]
    },
    defaultSwitchState() {
      return !this.lmeMedicines.length > 0
    },
    defaultAnamnesis() {
      if (this.attendance.disease.id === DISEASES.PSORIASIS) {
        const pasi = this.computedPasi === '?' ? 'indefinido' : this.computedPasi
        const bsa = this.computedBsa === '?' ? 'indefinido' : this.computedBsa
        const dlqi = this.computedDlqi === '?' ? 'indefinido' : this.computedDlqi

        const lme = `Paciente apresenta histórico de lesões eritematosas e descamativas compatíveis com diagnóstico de psoríase ${
          this.diseaseLevelText
        }${this.diagnosisTimeText} ${
          this.affectedAreasText
        }, com os seguintes critérios de gravidade: PASI (Psoriasis Area and Severity Index) ${pasi}, BSA (Body Surface Area) ${bsa}, DLQI (Dermatology Life Quality Index) ${dlqi}. (CID ${
          this.phaseData?.cid?.code || 'L40.0'
        }).${
          this.antiTnfOptions.find((o) => o.value === this.phaseData?.antitnf)?.label || ''
        } Realizado uso de tratamentos conforme segue abaixo por mais de 3 meses contínuos entretanto sem melhora efetiva da doença.`
        return lme
      } else if (this.attendance.disease.id === DISEASES.ESPONDILOARTRITIS) {
        return `Paciente em acompanhamento com diagnóstico de espondiloartrite ${
          this.symptomType[this.predominantSymptom] || 'indefinida'
        } segundo os critérios ASAS 2009 (CID:M07.0). ${
          this.computedCriteriosAtividadeText
        }. Teve falha aos MMCDs e AINEs apesar de tempo e doses adequados e, por isso, solicito o tratamento acima.\n${
          this.antiTnfOptions.find((o) => o.value === this.phaseData?.antitnf)?.label || ''
        }`
      }

      return this.antiTnfOptions.find((o) => o.value === this.phaseData?.antitnf)?.label || ''
    },
    defaultMedicineText() {
      const previousTreatmentsKey =
        this.attendance.disease.id === DISEASES.PSORIASIS ? 'dados_especificos' : 'historico_medicamentoso'
      let defaultText = Object.values(this.getPhaseDataByKey(previousTreatmentsKey)?.medicines || {})
        .map((m) => m.medicine.name)
        .join(', ')

      return defaultText
    },
    predominantSymptom() {
      return this.getPhaseDataByKey('sintomas')?.predominantSymptom
    },
    computedCriteriosAtividadeText() {
      let result = []

      if (this.predominantSymptom === 'AXIAL') {
        if (this.computedBasdai !== '?') result.push('BASDAI ' + this.computedBasdai)
        if (this.computedAsdasPcr !== '?') result.push('ASDAS PCR ' + this.computedAsdasPcr)
        if (this.computedAsdasVhs !== '?') result.push('ASDAS VHS ' + this.computedAsdasVhs)
      }
      if (this.predominantSymptom === 'PERIFERICO') {
        if (this.computedDas28 !== '?') result.push('DAS-28 ' + this.computedDas28)
        if (this.computedDapsa !== '?') result.push('DAPSA ' + this.computedDapsa)
      }
      if (this.computedEva !== '?') result.push('EVA ' + this.computedEva)

      if (this.getPhaseDataByKey('pasi_bsa')?.pasi != null)
        result.push('PASI ' + this.getPhaseDataByKey('pasi_bsa').pasi)
      if (this.getPhaseDataByKey('pasi_bsa')?.bsa != null) result.push('BSA ' + this.getPhaseDataByKey('pasi_bsa').bsa)

      if (result.length === 0) {
        return ''
      }

      return '\nCom os seguintes critérios: ' + result.join(', ')
    },
    printEmptyLmeUrl() {
      return `${this.publicStorageUrl}/lme/lme-vazio.pdf`
    },
    printLmeUrl() {
      return `${this.$util.apiURL()}/attendance/lme/print/${this.attendance.id}`
    },
  },
  data() {
    return {
      isLoaded: false,
      publicStorageUrl: PUBLIC_STORAGE_URL,
      emptyPrint: false,
      valid: false,
      symptomType: {
        AXIAL: 'axial',
        PERIFERICO: 'periférica',
      },
      antiTnfOptions: [
        { text: 'Sem restrição', value: 'SEM_RESTRICAO', label: '' },
        { text: 'Falha com anti-tnf', value: 'FALHA_ANTI_TNF', label: ' Apresenta falha para biológico anti-TNF.' },
        {
          text: 'Contraindicação para anti-tnf',
          value: 'CONTRAINDICACAO_ANTI_TNF',
          label: ' Apresenta contra indicação para biológico anti-TNF.',
        },
      ],
      error: false,
    }
  },
  async mounted() {
    this.emptyPrint = this.defaultSwitchState
    if (!!this.phaseData) {
      // encontre o nome da mãe na receita federal
      if (!this.attendance.patientMotherName) {
        const patientData = await this.$api.verifyCPF(this.attendance.patient.cpf)
        this.attendance.patientMotherName = patientData.verifiedUser.motherName
      }

      this.phaseData.patientMotherName = this.attendance.patientMotherName
    }
    this.phaseData.medicines = this.buildMedicinesObject()
    this.$nextTick(() => {
      this.isLoaded = true
    })
  },
  methods: {
    ...attendanceActionsMap(['startServerSyncing', 'endServerSyncing']),
    async resetAnamnesis() {
      if (await this.$root.confirm('Deseja gerar o texto da anamnese novamente? Isso sobrescreverá o texto atual!')) {
        this.phaseData.anamnesis = this.defaultAnamnesis
      }
    },
    async resetMedicineText() {
      if (
        await this.$root.confirm('Deseja gerar o texto de medicamentos novamente? Isso sobrescreverá o texto atual!')
      ) {
        this.phaseData.previousMedicineText = this.defaultMedicineText
      }
    },
    getDefaultData() {
      return {
        anamnesis: this.defaultAnamnesis,
        local: null,
        patientMotherName: this.attendance.patientMotherName,
        patientSkinColor: this.attendance.patientSkinColor,
        incapacity: false,
        responsibleName: null,
        medicines: this.buildMedicinesObject(),
        antitnf: null,
        printCurrentDate: false,
        cid: null,
        previousMedicineText: this.defaultMedicineText,
        emptyPrint: false,
      }
    },
    buildMedicinesObject() {
      let result = {}
      this.lmeMedicines.forEach((medicine) => {
        result[medicine.name] =
          !this.phaseData?.medicines || Object.keys(this.phaseData.medicines).length === 0
            ? {
                month1: medicine.quantities[0] || 0,
                month2: medicine.quantities[1] || 0,
                month3: medicine.quantities[2] || 0,
                month4: medicine.quantities[3] || 0,
                month5: medicine.quantities[4] || 0,
                month6: medicine.quantities[5] || 0,
                lmeId: medicine.lmeId,
              }
            : this.phaseData.medicines[medicine.name]?.lmeId === medicine.lmeId
              ? {
                  month1: this.phaseData.medicines[medicine.name]?.month1 || 0,
                  month2: this.phaseData.medicines[medicine.name]?.month2 || 0,
                  month3: this.phaseData.medicines[medicine.name]?.month3 || 0,
                  month4: this.phaseData.medicines[medicine.name]?.month4 || 0,
                  month5: this.phaseData.medicines[medicine.name]?.month5 || 0,
                  month6: this.phaseData.medicines[medicine.name]?.month6 || 0,
                  lmeId: medicine.lmeId,
                }
              : {
                  month1: medicine.quantities[0] || 0,
                  month2: medicine.quantities[1] || 0,
                  month3: medicine.quantities[2] || 0,
                  month4: medicine.quantities[3] || 0,
                  month5: medicine.quantities[4] || 0,
                  month6: medicine.quantities[5] || 0,
                  lmeId: medicine.lmeId,
                }
      })
      return result
    },
    resetResponsible() {
      this.phaseData.responsibleName = null
    },

    async saveMedicalDocument() {
      return this.$api.medicalDocument.create({
        patientId: this.attendance.patient.id,
        kind: 'lme',
        data: {},
        attendanceId: this.attendance.id,
      })
    },

    async saveLme() {
      if (!this.emptyPrint) {
        try {
          await this.saveMedicalDocument()
        } catch (e) {
          console.log(e)
        }
      }

      if (this.isServerSyncedData || this.isSavingData) {
        return
      }
      try {
        await this.startServerSyncing()
        await this.$api.patchAttendance(this.attendance.id, this.attendance)
        await this.endServerSyncing(true)
      } catch (error) {
        await this.endServerSyncing(false)
        if (showError) {
          this.$root.error(error)
        } else {
          this.$logger.warn('Não foi possível salvar os dados do atendimento no servidor!', error)
        }
      }

      return Promise.resolve({})
    },
  },
  watch: {
    selectedTreatments: {
      deep: true,
      handler(newVal, oldVal) {
        this.phaseData.medicines = this.buildMedicinesObject()
      },
    },
  },
}
</script>

<style scoped>
* :deep(.v-input--switch) {
  margin-top: 0;
}
</style>
